import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  type DetailedHTMLProps,
  type HTMLAttributes,
  type Ref,
} from 'react';

import type {
  JaakVisageElement,
  JaakVisageResult,
  JaakVisageStatus,
  JaakVisageError,
  JaakLicenseEnvironment,
  JaakVisageCamera,
  JaakVisageCaptureMode,
  JaakVisageLanguage,
  JaakVisageDetectionMode,
} from '@tankef/core';

import { Alert } from '../../primitives/Alert';
import { Button } from '../../primitives/Button';
import { Spinner } from '../../primitives/Spinner';
import { cn } from '../../utils';
import { JaakVisageCaptureStub } from '../_e2e-stubs/JaakVisageCaptureStub';

// Plan 08 F1: E2E stub. Ver `JaakDocumentCapture/index.tsx` para racional.
// 2026-05-29: añadido `VITE_KYC_LOCAL_STUB` (OR con VITE_E2E) para dev local
// que no puede pelear con CORS de services.api.jaak.ai. Misma sustitución
// por stub en cualquiera de los dos flags.
const E2E_MODE = (() => {
  const env = (
    import.meta as unknown as { env?: { VITE_E2E?: string; VITE_KYC_LOCAL_STUB?: string } }
  ).env;
  return env?.VITE_E2E === 'true' || env?.VITE_KYC_LOCAL_STUB === 'true';
})();

// ============================================================================
// JSX Declaration for WebComponent
// ============================================================================

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'jaak-visage': DetailedHTMLProps<
        HTMLAttributes<JaakVisageElement> & {
          license?: string;
          'license-environment'?: JaakLicenseEnvironment;
          'app-id'?: string;
          camera?: JaakVisageCamera;
          'capture-mode'?: JaakVisageCaptureMode;
          language?: JaakVisageLanguage;
          'detection-mode'?: JaakVisageDetectionMode;
          debug?: boolean;
          'enable-telemetry'?: boolean;
          'enable-metrics'?: boolean;
          ref?: Ref<JaakVisageElement>;
        },
        JaakVisageElement
      >;
    }
  }
}

// ============================================================================
// Constants
// ============================================================================

const JAAK_VISAGE_SDK_URL =
  'https://cdn.jsdelivr.net/npm/@jaak.ai/visage@1.6.1/dist/jaak-visage-webcomponent/jaak-visage-webcomponent.esm.js';
// SHA-384 of the pinned bundle. Verified against the upstream CDN on
// 2026-10-16; rotate together with the version above. If jsDelivr serves
// modified bytes the browser will refuse to execute, which is the whole
// point of pinning a third-party identity-capture SDK.
const JAAK_VISAGE_SDK_INTEGRITY =
  'sha384-IqdcYW6FpxRbFDJ1J+CA1deQmOkWo+JWF3EdPIKYa0uik5o5jGdRonpcJtN4zYC+';

// ============================================================================
// Types
// ============================================================================

export type JaakVisageStep =
  | 'loading' // Cargando SDK
  | 'ready' // Listo para capturar
  | 'capturing' // Capturando video
  | 'processing' // Procesando
  | 'complete' // Captura exitosa
  | 'error'; // Error

export interface JaakVisageCaptureLabels {
  loading?: string;
  readyTitle?: string;
  readyDescription?: string;
  capturingTitle?: string;
  capturingDescription?: string;
  processingTitle?: string;
  errorTitle?: string;
  retryButton?: string;
  startButton?: string;
  cancelButton?: string;
}

export interface JaakVisageCaptureHandle {
  startCapture: () => void;
  cancel: () => void;
}

export interface JaakVisageCaptureProps {
  /** License key de Jaak (requerido) */
  licenseKey: string;
  /** Ambiente de la licencia (requerido — no asumir 'sandbox' silenciosamente) */
  licenseEnvironment: JaakLicenseEnvironment;
  /** Callback cuando la captura se completa exitosamente */
  onCaptureComplete: (result: JaakVisageResult) => void;
  /** Callback cuando ocurre un error */
  onError?: (error: string) => void;
  /** Callback para cancelar */
  onCancel?: () => void;
  /** Notifica al padre cuando el step interno cambia */
  onStepChange?: (step: JaakVisageStep) => void;
  /** Si true, oculta los botones internos (el padre controla las acciones via ref) */
  hideActions?: boolean;
  /** Idioma de la interfaz del SDK */
  language?: JaakVisageLanguage;
  /** Modo de captura: auto o manual */
  captureMode?: JaakVisageCaptureMode;
  /** Cámara a usar (default: front para selfie) */
  camera?: JaakVisageCamera;
  /** Modo de detección facial */
  detectionMode?: JaakVisageDetectionMode;
  /**
   * Textos personalizables. Los defaults son genéricos en español pensados como
   * fallback de desarrollo — apps en producción deberían pasar todos los labels
   * traducidos via i18n.
   */
  labels?: JaakVisageCaptureLabels;
  /** Clases CSS adicionales */
  className?: string;

  // ── Web component config (mapeados a atributos de <jaak-visage>) ─────────
  /** Debug del SDK. */
  debug?: boolean;
  /** App ID de Jaak. */
  appId?: string;
  /** Habilitar telemetría del SDK. Default false. */
  enableTelemetry?: boolean;
  /** Habilitar métricas del SDK. Default false. */
  enableMetrics?: boolean;
}

// ============================================================================
// Default Labels — fallback de desarrollo. Los callers en producción deben
// pasar `labels` con strings traducidos via i18n.
// ============================================================================

const defaultLabels: Required<JaakVisageCaptureLabels> = {
  loading: 'Cargando cámara...',
  readyTitle: 'Captura tu selfie',
  readyDescription: 'Centra tu rostro dentro del marco y mantente quieto',
  capturingTitle: 'Capturando...',
  capturingDescription: 'Mantén tu rostro centrado',
  processingTitle: 'Procesando imagen...',
  errorTitle: 'Error al capturar',
  retryButton: 'Reintentar',
  startButton: 'Iniciar captura',
  cancelButton: 'Cancelar',
};

// ============================================================================
// SDK Loader
// ============================================================================

let visageSdkLoadPromise: Promise<void> | null = null;

function loadVisageSdk(): Promise<void> {
  if (visageSdkLoadPromise) return visageSdkLoadPromise;

  visageSdkLoadPromise = new Promise((resolve, reject) => {
    if (customElements.get('jaak-visage')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.src = JAAK_VISAGE_SDK_URL;
    script.integrity = JAAK_VISAGE_SDK_INTEGRITY;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => {
      // Reset so a future mount can retry the load instead of inheriting
      // the rejected promise forever.
      visageSdkLoadPromise = null;
      reject(new Error('Failed to load Jaak Visage SDK'));
    };
    document.head.appendChild(script);
  });

  return visageSdkLoadPromise;
}

// ============================================================================
// Component
// ============================================================================

const JaakVisageCaptureReal = forwardRef<JaakVisageCaptureHandle, JaakVisageCaptureProps>(
  function JaakVisageCapture(
    {
      licenseKey,
      licenseEnvironment,
      onCaptureComplete,
      onError,
      onCancel,
      onStepChange,
      hideActions = false,
      language = 'es',
      captureMode = 'auto',
      camera = 'front',
      detectionMode = 'auto',
      labels: customLabels,
      className,
      debug,
      appId,
      enableTelemetry = false,
      enableMetrics = false,
    }: JaakVisageCaptureProps,
    ref
  ) {
    const visageRef = useRef<JaakVisageElement | null>(null);
    const [step, setStep] = useState<JaakVisageStep>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string>('');

    const labels = { ...defaultLabels, ...customLabels };

    useEffect(() => {
      loadVisageSdk()
        .then(() => {
          setSdkLoaded(true);
        })
        .catch(err => {
          setStep('error');
          setErrorMessage(err.message || 'Error al cargar el SDK');
          onError?.(err.message);
        });
    }, [onError]);

    // Safely remove the Jaak Visage webcomponent from DOM.
    // Defense-in-depth: element.remove() should never throw, but the SDK's
    // disconnectedCallback has historically thrown when services weren't fully
    // initialized. Wrapping in try/catch prevents an unhandled error during
    // teardown.
    const safeStop = useCallback(() => {
      if (!visageRef.current) return;
      try {
        visageRef.current.remove();
      } catch {
        // Absorb SDK disconnectedCallback errors
      }
      visageRef.current = null;
    }, []);

    const handleVideoCaptured = useCallback(
      (event: Event) => {
        const customEvent = event as CustomEvent<JaakVisageResult>;
        safeStop();
        setStep('complete');
        onCaptureComplete(customEvent.detail);
      },
      [onCaptureComplete, safeStop]
    );

    const handleStatusUpdated = useCallback((event: Event) => {
      const customEvent = event as CustomEvent<JaakVisageStatus>;
      setStatusMessage(customEvent.detail.message);

      const status = customEvent.detail.status;
      if (status === 'ready') {
        setStep('ready');
      } else if (status === 'capturing') {
        setStep('capturing');
      } else if (status === 'processing') {
        setStep('processing');
      }
    }, []);

    const handleError = useCallback(
      (event: Event) => {
        const customEvent = event as CustomEvent<JaakVisageError>;
        safeStop();
        setStep('error');
        setErrorMessage(customEvent.detail.message);
        onError?.(customEvent.detail.message);
      },
      [onError, safeStop]
    );

    const handleCancel = useCallback(() => {
      safeStop();
      onCancel?.();
    }, [onCancel, safeStop]);

    useEffect(() => {
      if (!sdkLoaded) return;

      const element = visageRef.current;
      if (!element) return;

      element.addEventListener('videoCaptured', handleVideoCaptured);
      element.addEventListener('statusUpdated', handleStatusUpdated);
      element.addEventListener('error', handleError);

      // Iniciar la cámara cuando el elemento esté disponible. El custom element
      // puede no haber hecho upgrade aún (`.start` undefined), así que se hace
      // polling. CRÍTICO: el timer se guarda y se cancela en el cleanup — sin
      // esto, si `.start` nunca aparece (CDN/upgrade falla, o el elemento quedó
      // detached tras safeStop) la recursión seguía corriendo cada 100ms tras
      // el unmount, reteniendo el elemento y leakeando indefinidamente.
      let startTimer: ReturnType<typeof setTimeout> | undefined;
      let cancelled = false;
      const waitForStart = () => {
        if (cancelled) return;
        if (element.start) {
          element.start();
        } else {
          startTimer = setTimeout(waitForStart, 100);
        }
      };
      waitForStart();

      return () => {
        cancelled = true;
        if (startTimer) clearTimeout(startTimer);
        element.removeEventListener('videoCaptured', handleVideoCaptured);
        element.removeEventListener('statusUpdated', handleStatusUpdated);
        element.removeEventListener('error', handleError);
        safeStop();
      };
    }, [sdkLoaded, handleVideoCaptured, handleStatusUpdated, handleError, safeStop]);

    const startCapture = useCallback(() => {
      setStep('capturing');
      visageRef.current?.start();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        startCapture,
        cancel: handleCancel,
      }),
      [startCapture, handleCancel]
    );

    useEffect(() => {
      onStepChange?.(step);
    }, [step, onStepChange]);

    const handleRetry = () => {
      setErrorMessage('');
      setStatusMessage('');
      visageRef.current?.restart();
      setStep('ready');
    };

    const isLoadingState = step === 'loading';
    const isProcessingState = step === 'processing';

    return (
      <div className={cn('flex flex-col items-center', className)}>
        {/* Status Header — aria-live announces transitions to AT users */}
        <div
          className="mb-6 text-center"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-busy={isLoadingState || isProcessingState}
        >
          {step === 'loading' && (
            <>
              <Spinner size="lg" className="mx-auto mb-4" />
              <p className="text-tkf-fg-muted">{labels.loading}</p>
            </>
          )}

          {step === 'ready' && (
            <>
              <h2 className="mb-2 text-xl font-bold text-tkf-fg">{labels.readyTitle}</h2>
              <p className="text-sm text-tkf-fg-muted">{labels.readyDescription}</p>
            </>
          )}

          {step === 'capturing' && (
            <>
              <h2 className="mb-2 text-xl font-bold text-tkf-fg">{labels.capturingTitle}</h2>
              <p className="text-sm text-tkf-fg-muted">
                {statusMessage || labels.capturingDescription}
              </p>
            </>
          )}

          {step === 'processing' && (
            <>
              <Spinner size="lg" className="mx-auto mb-4" />
              <p className="text-tkf-fg-muted">{labels.processingTitle}</p>
            </>
          )}

          {step === 'error' && (
            <Alert variant="error" title={labels.errorTitle} className="mx-auto max-w-sm text-left">
              {errorMessage}
            </Alert>
          )}
        </div>

        {/* WebComponent Container */}
        {sdkLoaded && step !== 'error' && (
          <div
            className={cn(
              'relative aspect-[3/4] w-full max-w-sm overflow-hidden rounded-lg bg-black',
              isLoadingState && 'animate-pulse'
            )}
          >
            <jaak-visage
              ref={visageRef}
              license={licenseKey}
              license-environment={licenseEnvironment}
              {...(appId !== undefined ? { 'app-id': appId } : {})}
              language={language}
              capture-mode={captureMode}
              camera={camera}
              detection-mode={detectionMode}
              {...(debug !== undefined ? { debug } : {})}
              enable-telemetry={enableTelemetry}
              enable-metrics={enableMetrics}
            />
          </div>
        )}

        {/* Action Buttons — hidden when parent controls them via hideActions */}
        {!hideActions && (
          <div className="mt-6 flex w-full max-w-sm gap-4">
            {step === 'ready' && captureMode === 'manual' && (
              <>
                {onCancel && (
                  <Button variant="outline" className="flex-1" onClick={handleCancel}>
                    {labels.cancelButton}
                  </Button>
                )}
                <Button variant="primary" className="flex-1" onClick={startCapture}>
                  {labels.startButton}
                </Button>
              </>
            )}

            {step === 'ready' && captureMode === 'auto' && onCancel && (
              <Button variant="outline" className="flex-1" onClick={handleCancel}>
                {labels.cancelButton}
              </Button>
            )}

            {step === 'capturing' && onCancel && (
              <Button variant="outline" className="flex-1" onClick={handleCancel}>
                {labels.cancelButton}
              </Button>
            )}

            {step === 'error' && (
              <>
                {onCancel && (
                  <Button variant="outline" className="flex-1" onClick={handleCancel}>
                    {labels.cancelButton}
                  </Button>
                )}
                <Button variant="primary" className="flex-1" onClick={handleRetry}>
                  {labels.retryButton}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);

// Plan 08 F1: ver `JaakDocumentCapture/index.tsx` para racional del
// export condicional.
const JaakVisageCaptureE2EWrapper = forwardRef<JaakVisageCaptureHandle, JaakVisageCaptureProps>(
  function JaakVisageCaptureE2EWrapper(props, _ref) {
    return <JaakVisageCaptureStub {...props} />;
  }
);

export const JaakVisageCapture = E2E_MODE ? JaakVisageCaptureE2EWrapper : JaakVisageCaptureReal;
